import {
  CreateParams,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  HttpError,
  SortPayload,
  fetchUtils,
  addRefreshAuthToDataProvider,
  GetManyResult,
  GetOneParams,
  UpdateParams,
  UpdateManyParams,
  DeleteParams,
  DeleteManyParams,
} from 'react-admin';
import jsonServerProvider from 'ra-data-json-server';
import { captureException } from '@sentry/react';
import { chunk } from 'lodash';

import { authTokenStore } from '../authTokenStore';
import { refreshAuth } from '../Authprovider';
import isExpectedAPIError from './isExpectedAPIError';
import resourceNames from '../../constants/resourceNames';

type RestrictedOptions = fetchUtils.Options & {
  headers?: Headers;
}

const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
  const finalOptions = {
    ...options,
    credentials: 'include',
  } as RestrictedOptions;
  if (!finalOptions.headers) {
    finalOptions.headers = new Headers({ Accept: 'application/json' });
  }
  const token = authTokenStore.getToken();
  finalOptions.headers.set('authorization', `Bearer ${token}`);

  try {
    const result = await fetchUtils.fetchJson(url, finalOptions);
    return result;
  } catch (e) {
    if (e instanceof HttpError) {
      if (!isExpectedAPIError(e)) {
        const extra = {
          url,
          options: {
            ...finalOptions,
            headers: Object.fromEntries(finalOptions.headers),
          },
          response: e.body,
          status: e.status,
        };
        const tags = {
          status: e.status,
        };
        captureException(e, { extra, tags });
      }
      // Remove errors field from error responses as it is expected in a different format by
      // react-admin and used to populate form errors
      e.body = { ...e.body, errors: undefined };
    } else {
      captureException(e);
    }
    throw e;
  }
};

const getFinalResourcePath = (resource: string) => {
  if (resource === resourceNames.MEMBERS) return 'members';
  if (resource === resourceNames.API_KEYS) return 'api-keys';
  if (resource === resourceNames.PERMISSION_ROLES) return 'permission-roles';

  return resource;
};

const getDataProvider = (apiUrl: string) => {
  const defaultDataProvider = jsonServerProvider(apiUrl, httpClient);
  const overriddenDataProvider = {
    getList: (resource: string, params: GetListParams) => {
      if (resource === resourceNames.REQUESTS && !params.filter?.requestType) {
        return Promise.resolve({ data: [], total: 0 });
      }
      const isDefaultSort = params.sort?.field === 'id';
      return defaultDataProvider.getList(getFinalResourcePath(resource), {
        ...params,
        sort: isDefaultSort ? {} as SortPayload : params.sort,
      });
    },
    getOne: async (resource: string, params: GetOneParams) => {
      const { data } = await defaultDataProvider.getOne(getFinalResourcePath(resource), params);
      return { data: { id: params.id, ...data } };
    },
    getManyReference: (resource: string, params: GetManyReferenceParams) => {
      const isDefaultSort = params.sort.field === 'id';
      return defaultDataProvider.getManyReference(getFinalResourcePath(resource), {
        ...params,
        sort: isDefaultSort ? {} as SortPayload : params.sort,
      });
    },
    getMany: async (
      resource:string,
      params: GetManyParams,
    ) => {
      const chunks = chunk(params.ids, 50);
      const results = await Promise.all(
        chunks.map((idsChunk) => defaultDataProvider.getList(getFinalResourcePath(resource), {
          filter: { id: idsChunk },
          pagination: { perPage: idsChunk.length, page: 1 },
          sort: {} as SortPayload,
        })),
      );
      const aggregatedResults = results.reduce((acc, result) => ({
        ...acc,
        data: [...acc.data, ...result.data],
      }), {
        data: [],
      } as GetManyResult);
      return aggregatedResults;
    },
    create: async (resource: string, params: CreateParams) => {
      const { json } = await httpClient(`${apiUrl}/${getFinalResourcePath(resource)}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });

      return {
        data: {
          id: json.id ?? window.crypto.getRandomValues(new Uint32Array(1))[0].toString(16),
          ...json,
        },
      };
    },
    update: (resource: string, params: UpdateParams) => defaultDataProvider
      .update(getFinalResourcePath(resource), params),
    patch: async (resource: string, params: { data: unknown }) => {
      const { json } = await httpClient(`${apiUrl}/${getFinalResourcePath(resource)}`, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      });
      return { data: json };
    },
    updateMany: (resource: string, params: UpdateManyParams) => defaultDataProvider
      .updateMany(getFinalResourcePath(resource), params),
    delete: (resource: string, params: DeleteParams) => defaultDataProvider
      .delete(getFinalResourcePath(resource), params),
    deleteMany: (resource: string, params: DeleteManyParams) => defaultDataProvider
      .deleteMany(getFinalResourcePath(resource), params),
  };

  return addRefreshAuthToDataProvider(overriddenDataProvider, refreshAuth);
};

export default getDataProvider;
